import { ITab } from "@/components/tabs/interface";
import { useStyle } from "./style";
import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import { QuotesListPageWidget } from "@/pages-components/quotes/quotes";
import { useTranslation } from "react-i18next";
import { Permissions } from "@/components/CheckPermission/enum";
import { useUserPermission } from "@/hooks/use-permission";
import { useState, useEffect } from "react";
import { FONT_FAMILY } from "@/utils/font-family";
import { Stack } from "@mui/material";
import { ClientBalanceComponent } from "@/components/client-balance/client-balance";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getItem, setItem } from "@/services/storage-data";
import { useTheme } from "@/contexts/theme/useTheme";

const StyledTabs = styled(Tabs)(() => {
  return {
    minHeight: "48px",
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      gap: "8px",
    },
  };
});

const StyledTab = styled(Tab)(() => {
  const { primaryColor, secondColor, neutralColor } = useGomakeTheme();
  const primaryColorValue = primaryColor(500);
  const primaryLight = primaryColor(100);
  const primaryDisabled = primaryColor(200);

  return {
    minWidth: "auto",
    minHeight: "40px",
    padding: "8px 20px",
    textTransform: "none",
    borderRadius: "8px",
    backgroundColor: "transparent",
    color: neutralColor(600),
    border: `1px solid ${neutralColor(200)}`,
    ...FONT_FAMILY.Inter(500, 15),
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: `var(--theme-secondary-100, ${primaryLight})`,
      color: `var(--theme-secondary-color, ${primaryColorValue})`,
      borderColor: `var(--theme-secondary-color, ${primaryDisabled})`,
    },
    "&.Mui-selected": {
      backgroundColor: secondColor(500),
      color: "#FFFFFF",
      borderColor: "transparent",
      ...FONT_FAMILY.Inter(600, 15),
      "&:hover": {
        backgroundColor: secondColor(500),
        color: "#FFFFFF",
        borderColor: "transparent",
      },
    },
  };
});

// HP Client theme: underlined tabs (Track + Highlight), Forma DJR UI, 48px height
// Track (borderBottom) lives on the parent Stack so it spans full width.
const HpStyledTabs = styled(Tabs)({
  minHeight: "48px",
  zIndex: 2,
  marginBottom: "-2px",
  "& .MuiTabs-flexContainer": {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 0,
    gap: 0,
  },
  "& .MuiTabs-indicator": {
    height: "2px",
    backgroundColor: "#024AD8",
    borderRadius: "4px",
    zIndex: 2,
  },
});

const HpStyledTab = styled(Tab)({
  minWidth: "72px",
  height: "48px",
  padding: "14px 16px",
  textTransform: "none",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  fontFamily: '"Forma DJR UI", sans-serif',
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "20px",
  color: "#1A1A1A",
  "&.Mui-selected": {
    color: "#024AD8",
  },
});

const HomeTableWidget = ({
  clientId,
  clientBalance,
  hideBalanceInToolbar = false,
}: {
  clientId: string;
  clientBalance?: number;
  hideBalanceInToolbar?: boolean;
}) => {
  const { classes } = useStyle();
  const { t } = useTranslation();
  const { CheckPermission } = useUserPermission();
  const { currentTheme } = useTheme();
  const isHpClient = currentTheme === "hp-client";

  const tabs: (ITab & { permission?: boolean })[] = [
    {
      title: t("home.tabs.Quotes"),
      permission: true,
      component: (
        <QuotesListPageWidget
          documentType={DOCUMENT_TYPE.quote}
          isFromHomePage={true}
        />
      ),
    },
    {
      title: t("home.tabs.Orders"),
      permission: true,
      component: (
        <QuotesListPageWidget
          documentType={DOCUMENT_TYPE.order}
          isFromHomePage={true}
        />
      ),
    },
    {
      title: t("tabs.invoices"),
      permission: CheckPermission(Permissions.SHOW_INVOICES),
      component: (
        <QuotesListPageWidget
          documentType={DOCUMENT_TYPE.invoice}
          isFromHomePage={true}
        />
      ),
    },
    {
      title: t("tabs.deliveryNotes"),
      permission: CheckPermission(Permissions.SHOW_DELIVERY_NOTES),
      component: (
        <QuotesListPageWidget
          documentType={DOCUMENT_TYPE.deliveryNote}
          isFromHomePage={true}
        />
      ),
    },
    {
      title: t("tabs.receipts"),
      permission: CheckPermission(Permissions.SHOW_RECEIPTS),
      component: (
        <QuotesListPageWidget
          documentType={DOCUMENT_TYPE.receipt}
          isFromHomePage={true}
        />
      ),
    },
  ].filter((tab) => tab.permission);

  const STORAGE_KEY = "homePageDocumentTab";

  // Initialize selectedTab from localStorage or default to 0
  const getInitialTab = (tabsLength: number): number => {
    try {
      const savedTabStr = getItem(STORAGE_KEY);
      if (savedTabStr) {
        const savedTab = JSON.parse(savedTabStr);
        if (typeof savedTab === "number" && savedTab >= 0) {
          // Validate that the saved tab index is within bounds
          return Math.max(0, Math.min(savedTab, tabsLength - 1));
        }
      }
    } catch (error) {
      // If parsing fails, default to 0
      console.warn("Failed to load saved tab from localStorage:", error);
    }
    return 0;
  };

  const [selectedTab, setSelectedTab] = useState<number>(() =>
    getInitialTab(tabs.length),
  );

  // Save tab to localStorage whenever it changes
  useEffect(() => {
    setItem(STORAGE_KEY, JSON.stringify(selectedTab));
  }, [selectedTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const _renderSelectedTab = () => {
    const TabsRoot = isHpClient ? HpStyledTabs : StyledTabs;
    const TabItem = isHpClient ? HpStyledTab : StyledTab;
    return (
      <TabsRoot
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="document type tabs"
      >
        {tabs.map((tab, index) => (
          <TabItem key={`tab-${index}`} label={tab.title} />
        ))}
      </TabsRoot>
    );
  };

  const _renderTabContent = () => {
    return (
      <div style={classes.mainContainer}>
        <div style={{ width: "100%" }}>
          {tabs.map((tab, index) => (
            <div key={index}>{index === selectedTab && tab.component}</div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{
          marginBottom: "12px",
          ...(isHpClient && {
            borderBottom: "2px solid #E8E8E8",
          }),
        }}
      >
        {_renderSelectedTab()}
        {!hideBalanceInToolbar && (
          <ClientBalanceComponent
            clientId={clientId}
            clientBalance={clientBalance}
          />
        )}
      </Stack>
      {_renderTabContent()}
    </>
  );
};

export { HomeTableWidget };
